import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import {
  Upload,
  FileText,
  Image,
  File,
  Download,
  Trash2,
  X,
  Loader2
} from 'lucide-react';

const FILE_ICONS = {
  pdf: FileText,
  doc: FileText,
  docx: FileText,
  xls: FileText,
  xlsx: FileText,
  txt: FileText,
  csv: FileText,
  png: Image,
  jpg: Image,
  jpeg: Image,
  gif: Image,
  zip: File,
  rar: File
};

export const DocumentManager = ({ entityType, entityId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [descripcion, setDescripcion] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (entityId) {
      loadDocuments();
    }
  }, [entityId]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/documents/entity/${entityType}/${entityId}`);
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Error cargando documentos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tamaño (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo es demasiado grande. Máximo 10MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', entityType);
      formData.append('entity_id', entityId);
      if (descripcion) {
        formData.append('descripcion', descripcion);
      }

      await api.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Documento subido correctamente');
      setDescripcion('');
      loadDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir documento');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc.id}/download`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: doc.content_type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error al descargar documento');
    }
  };

  const handleDelete = async (docId) => {
    if (!confirm('¿Eliminar este documento?')) return;

    try {
      await api.delete(`/documents/${docId}`);
      toast.success('Documento eliminado');
      loadDocuments();
    } catch (error) {
      toast.error('Error al eliminar documento');
    }
  };

  const getFileIcon = (extension) => {
    const IconComponent = FILE_ICONS[extension] || File;
    return IconComponent;
  };

  return (
    <Card className="p-4 bg-white border-slate-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-slate-900">
          Documentos Adjuntos
        </h3>
        <span className="text-sm text-slate-500">
          {documents.length} archivo{documents.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Upload Section */}
      <div className="mb-4 p-4 border-2 border-dashed border-slate-200 rounded-lg hover:border-blue-400 transition-colors">
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleUpload}
          className="hidden"
          id={`file-upload-${entityId}`}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.png,.jpg,.jpeg,.gif,.zip,.rar"
        />
        
        <div className="flex flex-col md:flex-row items-center gap-3">
          <Input
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            placeholder="Descripción (opcional)"
            className="flex-1"
            style={{ backgroundColor: '#ffffff' }}
          />
          <label
            htmlFor={`file-upload-${entityId}`}
            className="cursor-pointer"
          >
            <Button
              type="button"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
            >
              {uploading ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <Upload size={16} className="mr-2" />
              )}
              {uploading ? 'Subiendo...' : 'Subir Archivo'}
            </Button>
          </label>
        </div>
        <p className="text-xs text-slate-400 mt-2 text-center">
          PDF, DOC, XLS, imágenes, ZIP. Máximo 10MB
        </p>
      </div>

      {/* Documents List */}
      {loading ? (
        <div className="text-center py-4 text-slate-400">
          <Loader2 size={24} className="mx-auto animate-spin" />
        </div>
      ) : documents.length > 0 ? (
        <div className="space-y-2">
          {documents.map((doc) => {
            const FileIcon = getFileIcon(doc.extension);
            
            return (
              <div
                key={doc.id}
                className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="p-2 bg-white rounded-lg border border-slate-200">
                    <FileIcon size={20} className="text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 text-sm truncate">
                      {doc.filename}
                    </p>
                    <p className="text-xs text-slate-500">
                      {doc.size_formatted} • {new Date(doc.created_at).toLocaleDateString('es-AR')}
                    </p>
                    {doc.descripcion && (
                      <p className="text-xs text-slate-400 truncate">{doc.descripcion}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 ml-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload(doc)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Download size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(doc.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-6 text-slate-400">
          <File size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">No hay documentos adjuntos</p>
        </div>
      )}
    </Card>
  );
};

export default DocumentManager;
